import React from "react";
import { ArrowLeft, Clock, User } from "lucide-react";
import { Button } from "./ui/button";

const BlogPost2 = ({ onBack }) => {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Button 
        variant="ghost" 
        onClick={onBack}
        className="mb-8 text-credentra-bright-teal hover:text-credentra-dark-teal"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Blog
      </Button>
      
      <article className="prose prose-lg max-w-none">
        <header className="mb-8">
          <h1 className="text-4xl font-bold text-credentra-dark-teal mb-4">
            POS → Payroll → IRS: A Cleaner Data Path for Credits
          </h1>
          <div className="flex items-center gap-4 text-gray-600 text-sm">
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              4 min read
            </div>
            <div className="flex items-center gap-1">
              <User className="h-4 w-4" />
              Credentra Team
            </div>
          </div>
        </header>

        <div className="space-y-6 text-gray-700 leading-relaxed">
          <p className="text-xl text-gray-600 font-medium">
            The biggest headache in claiming FICA tip credits? Getting clean, accurate data from three different systems that don't talk to each other. Here's how modern integration is changing the game.
          </p>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">The Traditional Nightmare</h2>
          <p>
            Picture this: It's year-end, and your CPA asks about tip credit claims. Now you're digging through:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Monthly POS reports (hopefully you saved them all)</li>
            <li>Payroll records from your payroll provider</li>
            <li>Bank deposits and cash handling records</li>
            <li>Employee tip reporting logs</li>
          </ul>

          <p>
            Then comes the fun part: trying to match tip amounts from your POS system to what actually got reported in payroll. Spoiler alert: they rarely match perfectly, and figuring out why takes hours of detective work.
          </p>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">Why Systems Don't Play Nice</h2>
          <p>
            Each system serves a different purpose, so they track information differently:
          </p>

          <div className="bg-gray-50 p-6 rounded-lg my-6">
            <h3 className="font-bold text-credentra-dark-teal mb-3">Your POS System Says:</h3>
            <p className="mb-4">"Customer paid $50 for dinner and left a $10 tip on their credit card."</p>
            
            <h3 className="font-bold text-credentra-dark-teal mb-3">Your Payroll System Says:</h3>
            <p className="mb-4">"Employee reported $8.50 in tips this shift" (because they forgot about cash tips or had tip pooling).</p>
            
            <h3 className="font-bold text-credentra-dark-teal mb-3">The IRS Wants to Know:</h3>
            <p>"What was the exact employer FICA tax paid on reported tips, and can you prove it?"</p>
          </div>

          <p>
            See the problem? Three different numbers for what should be the same thing.
          </p>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">The Clean Data Solution</h2>
          <p>
            Modern integration tools solve this by creating a direct data pipeline that:
          </p>

          <ol className="list-decimal pl-6 space-y-3">
            <li><strong>Connects to your existing systems</strong> - No need to change your POS or payroll provider</li>
            <li><strong>Automatically reconciles discrepancies</strong> - Flags potential issues before they become problems</li>
            <li><strong>Maintains an audit trail</strong> - Everything is documented for IRS requirements</li>
            <li><strong>Generates compliant reports</strong> - Formats data exactly how the IRS expects to receive it</li>
          </ol>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">What This Means for Restaurant Owners</h2>
          <p>
            Instead of spending days gathering paperwork, you get:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Automatic data collection from day one</li>
            <li>Real-time visibility into potential credits</li>
            <li>Clean reports ready for your CPA</li>
            <li>Confidence that nothing fell through the cracks</li>
          </ul>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">What This Means for CPAs</h2>
          <p>
            No more playing detective with mismatched data. You receive:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Pre-reconciled data with source documentation</li>
            <li>Clear explanations of any adjustments or assumptions</li>
            <li>IRS-ready forms and supporting schedules</li>
            <li>Audit trail documentation if questions arise later</li>
          </ul>

          <div className="bg-credentra-bright-teal/10 border-l-4 border-credentra-bright-teal p-6 my-8">
            <h3 className="font-semibold text-credentra-dark-teal mb-2">Real Talk:</h3>
            <p>
              The biggest benefit isn't just time savings (though that's huge). It's the confidence that comes from knowing your data is accurate and complete. No more wondering if you missed something or if the IRS will question your calculations.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">Making It Work in Practice</h2>
          <p>
            The best integrations are invisible to your daily operations. You keep using the same POS system, the same payroll provider, the same processes your team already knows. The integration works in the background, quietly organizing everything for maximum tax benefit.
          </p>

          <p>
            Think of it like having a really good bookkeeper who never takes a day off, never makes arithmetic errors, and always stays up-to-date on tax law changes. Except this bookkeeper works 24/7 and costs a fraction of what manual processing would take.
          </p>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">The Bottom Line</h2>
          <p>
            Clean data isn't just about making your CPA's life easier (though they'll definitely appreciate it). It's about maximizing your tax benefits while minimizing your compliance risk. When your systems work together seamlessly, claiming credits becomes automatic instead of accidental.
          </p>

          <p>
            And that means more money stays in your business where it belongs, instead of getting lost in spreadsheet chaos or missed deadlines.
          </p>
        </div>
      </article>
    </div>
  );
};

export default BlogPost2;